import React, { useCallback, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { UploadedFile, SupportedFileType, SUPPORTED_EXTENSIONS, ProcessingStatus } from '../types/multimodal';
import { processFile, validateFile } from '../services/fileProcessingService';

interface FileUploadProps {
  files: UploadedFile[];
  onFilesChange: (files: UploadedFile[]) => void;
  maxFiles?: number;
  maxFileSize?: number;
  enabledTypes?: SupportedFileType[];
  className?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  files,
  onFilesChange,
  maxFiles = 5,
  maxFileSize = 10 * 1024 * 1024, // 10MB
  enabledTypes = ['image', 'document'],
  className = ''
}) => {
  const { t } = useTranslation();
  const { isPortuguese } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [isDragOver, setIsDragOver] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<Record<string, ProcessingStatus>>({});

  // Determinar tipo de arquivo baseado na extensão
  const getFileType = (fileName: string): SupportedFileType | null => {
    const ext = '.' + fileName.split('.').pop()?.toLowerCase();
    
    for (const [type, extensions] of Object.entries(SUPPORTED_EXTENSIONS)) {
      const extensionArray = extensions as readonly string[];
      if (extensionArray.includes(ext)) {
        return type as SupportedFileType;
      }
    }
    return null;
  };

  // Processar arquivo individual
  const processIndividualFile = async (file: File): Promise<UploadedFile | null> => {
    const fileType = getFileType(file.name);
    if (!fileType || !enabledTypes.includes(fileType)) {
      throw new Error(t('fileUpload.unsupportedType', `Unsupported file type: ${file.type}`));
    }

    if (!validateFile(file, fileType, maxFileSize)) {
      throw new Error(t('fileUpload.fileTooLarge', `File is too large. Maximum size: ${Math.round(maxFileSize / (1024 * 1024))}MB`));
    }

    const uploadedFile: UploadedFile = {
      id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      type: fileType,
      name: file.name,
      size: file.size,
      processed: false
    };

    // Atualizar status de processamento
    setProcessingStatus(prev => ({
      ...prev,
      [uploadedFile.id]: {
        fileId: uploadedFile.id,
        status: 'processing',
        progress: 0,
        message: t('fileUpload.processing', 'Processing file...')
      }
    }));

    try {
      // Processar arquivo
      const processedFile = await processFile(uploadedFile, (progress) => {
        setProcessingStatus(prev => ({
          ...prev,
          [uploadedFile.id]: {
            ...prev[uploadedFile.id],
            progress
          }
        }));
      });

      // Atualizar status para completo
      setProcessingStatus(prev => ({
        ...prev,
        [uploadedFile.id]: {
          fileId: uploadedFile.id,
          status: 'completed',
          progress: 100,
          message: t('fileUpload.completed', 'Processing completed')
        }
      }));

      return processedFile;
    } catch (error) {
      // Atualizar status para erro
      setProcessingStatus(prev => ({
        ...prev,
        [uploadedFile.id]: {
          fileId: uploadedFile.id,
          status: 'error',
          progress: 0,
          message: error instanceof Error ? error.message : t('fileUpload.error', 'Processing failed')
        }
      }));
      
      throw error;
    }
  };

  // Manipular arquivos adicionados
  const handleFiles = async (newFiles: File[]) => {
    if (files.length + newFiles.length > maxFiles) {
      alert(t('fileUpload.tooManyFiles', `Maximum ${maxFiles} files allowed`));
      return;
    }

    const processedFiles: UploadedFile[] = [];
    
    for (const file of newFiles) {
      try {
        const processedFile = await processIndividualFile(file);
        if (processedFile) {
          processedFiles.push(processedFile);
        }
      } catch (error) {
        console.error('Error processing file:', file.name, error);
        alert(`Error processing ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    if (processedFiles.length > 0) {
      onFilesChange([...files, ...processedFiles]);
    }
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  }, [files, maxFiles]);

  // File input handler
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    handleFiles(selectedFiles);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remover arquivo
  const removeFile = (fileId: string) => {
    onFilesChange(files.filter(f => f.id !== fileId));
    
    // Limpar status de processamento
    setProcessingStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[fileId];
      return newStatus;
    });
  };

  // Obter extensões suportadas como string
  const getSupportedExtensions = (): string => {
    return enabledTypes.flatMap(type => SUPPORTED_EXTENSIONS[type] as readonly string[]).join(',');
  };

  // Renderizar ícone do tipo de arquivo
  const renderFileTypeIcon = (type: SupportedFileType) => {
    switch (type) {
      case 'image':
        return '🖼️';
      case 'document':
        return '📄';
      case 'audio':
        return '🎵';
      case 'video':
        return '🎥';
      default:
        return '📁';
    }
  };

  // Formatear tamanho do arquivo
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
          isDragOver
            ? 'border-purple-500 bg-purple-50'
            : files.length === 0
            ? 'border-gray-300 hover:border-purple-400'
            : 'border-gray-200'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={getSupportedExtensions()}
          onChange={handleFileInput}
          className="hidden"
        />
        
        <div className="space-y-4">
          <div className="text-4xl">📎</div>
          <div>
            <p className="text-lg font-medium text-gray-700">
              {t('fileUpload.dragDrop', 'Drag and drop files here')}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {t('fileUpload.orClick', 'or click to select files')}
            </p>
          </div>
          
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors duration-200"
          >
            {t('fileUpload.selectFiles', 'Select Files')}
          </button>
          
          <div className="text-xs text-gray-400">
            <p>{t('fileUpload.maxFiles', `Maximum ${maxFiles} files`)}</p>
            <p>{t('fileUpload.maxSize', `Maximum size: ${Math.round(maxFileSize / (1024 * 1024))}MB each`)}</p>
            <p>
              {t('fileUpload.supportedTypes', 'Supported types')}: {' '}
              {enabledTypes.map(type => (SUPPORTED_EXTENSIONS[type] as readonly string[]).join(', ')).join(', ')}
            </p>
          </div>
        </div>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-medium text-gray-800">
            {t('fileUpload.uploadedFiles', 'Uploaded Files')} ({files.length}/{maxFiles})
          </h3>
          
          {files.map((file) => {
            const status = processingStatus[file.id];
            return (
              <div key={file.id} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-2xl">{renderFileTypeIcon(file.type)}</div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {file.name}
                    </p>
                    <span className="text-xs text-gray-500">
                      ({formatFileSize(file.size)})
                    </span>
                  </div>
                  
                  {/* Processing Status */}
                  {status && (
                    <div className="mt-1">
                      {status.status === 'processing' && (
                        <div className="flex items-center space-x-2">
                          <div className="w-32 bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-purple-500 h-1.5 rounded-full transition-all duration-200"
                              style={{ width: `${status.progress}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600">{status.progress}%</span>
                        </div>
                      )}
                      {status.status === 'completed' && (
                        <div className="flex items-center space-x-1 text-green-600">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-xs">{t('fileUpload.processed', 'Processed')}</span>
                        </div>
                      )}
                      {status.status === 'error' && (
                        <div className="flex items-center space-x-1 text-red-600">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <span className="text-xs">{t('fileUpload.error', 'Error')}</span>
                        </div>
                      )}
                      {status.message && (
                        <p className="text-xs text-gray-500 mt-1">{status.message}</p>
                      )}
                    </div>
                  )}

                  {/* File metadata */}
                  {file.metadata && (
                    <div className="mt-1 text-xs text-gray-500">
                      {file.metadata.width && file.metadata.height && (
                        <span>{file.metadata.width} × {file.metadata.height}px</span>
                      )}
                      {file.metadata.duration && (
                        <span>{Math.round(file.metadata.duration)}s</span>
                      )}
                      {file.metadata.pages && (
                        <span>{file.metadata.pages} pages</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Remove Button */}
                <button
                  onClick={() => removeFile(file.id)}
                  className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors duration-200"
                  title={t('fileUpload.removeFile', 'Remove file')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};